/**
 * PdfPagePinViewer — renders a PDF (with pdf.js) in a scrollable, zoomable
 * canvas, lets the user click anywhere to drop a pin, and exposes the
 * pin'd page as a base64 PNG so a backend can run Claude Vision on it.
 *
 * Why not just `<iframe src=pdf>`? Iframes give you the native browser
 * viewer (good for browsing), but no click coordinates and no programmatic
 * access to the rendered pixels. For "click on your parcel" UX we need
 * both — pdf.js is the standard tool.
 *
 * Performance notes :
 *   - Pages are rendered lazily (only the active page is in the DOM).
 *     Multi-page graphique PDFs (5+ plans for very large communes) stay
 *     usable on 8 GB laptops.
 *   - pdf.js worker URL is resolved by Vite at build time via `?url`.
 *     No CDN, no manual copy.
 *   - Canvas pixel ratio = `devicePixelRatio` for crisp rendering on
 *     retina screens; the `style` width/height is kept at logical pixels
 *     so layout/click coordinates match.
 *
 * Pin output : when the user clicks, we draw a high-contrast marker on
 * the canvas at that point, then expose the canvas as `image/png` base64
 * via the `onPin` callback. The page is sent as-is (with marker drawn) so
 * Claude can see exactly where the user pointed without any coordinate
 * negotiation.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as pdfjsLib from "pdfjs-dist";
// Vite's `?url` import gives us a hashed URL for the worker bundled at
// build time — works in dev and prod without any pdfjs CDN.
import workerSrc from "pdfjs-dist/build/pdf.worker.mjs?url";
import { Button } from "./ui";
import { Ic } from "./icons";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

// Base render scale — multiplied by user zoom. 1.0 = pdf.js default size
// (the page at "logical" PDF units). Combined with zoom + DPR, the actual
// canvas pixel count can balloon on A0 plans, so we cap the export size
// independently (cf. EXPORT_MAX_DIM below) to stay under the backend's
// image cap.
const BASE_RENDER_SCALE = 1.5;
const ZOOM_LEVELS = [0.5, 0.75, 1, 1.5, 2, 2.5, 3] as const;
const DEFAULT_ZOOM_INDEX = 2; // = 1.0
// ─── Export rendering (decoupled from display) ─────────────────────────
// Critical : the image we send to Claude is rendered FRESH on every pin,
// at a fixed scale, independent of the user's zoom level. Without this,
// zooming in to 200 %+ on a large plan blew the canvas up so much that
// the post-downscale export had a < 1 px marker AND no legend visible —
// Claude was forced to guess the zone colour and routinely hallucinated.
//
// Fixed scale 2.5 × native PDF size + JPEG @ 85 % keeps the legend
// readable on most plans while staying well under the backend's 4 MB
// base64 cap (typical A0 export ≈ 800 kB JPEG = 1.1 MB base64).
const EXPORT_RENDER_SCALE = 2.5;
const EXPORT_MAX_DIM = 2400;
const EXPORT_JPEG_QUALITY = 0.85;
// Marker visual config. Critically NEUTRAL (black + white only) — early
// versions used a magenta marker which Claude Vision routinely confused
// with the zone colour itself ("the marker is in a pink area = pink
// zone"). PLU plans virtually never use pure black/white as a zoning
// fill, so a black-and-white reticle stays visually unambiguous as an
// annotation rather than a colour reference.
const MARKER_RADIUS = 11;
const MARKER_RING_WIDTH = 2;
const MARKER_HALO_WIDTH = 5;

export type PinPayload = {
  /** 1-based page index the user pinned. */
  page: number;
  /** Pixel coords of the pin within the rendered canvas (already includes
   *  device pixel ratio). Useful for showing the marker between renders. */
  xPx: number;
  yPx: number;
  /** Base64 PNG of the page with the marker drawn. Sent to backend. */
  imageBase64: string;
};

type Props = {
  /** PDF URL — typically a Firebase Storage download URL. */
  pdfUrl: string;
  /** Fired every time the user clicks (drops a new pin). */
  onPin: (payload: PinPayload) => void;
  /** When set, the busy state on the parent (Claude call running) — we
   *  visually indicate it on the canvas. */
  busy?: boolean;
  className?: string;
};

export function PdfPagePinViewer({ pdfUrl, onPin, busy, className }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Hold a hidden offscreen copy of the rendered page WITHOUT the marker.
  // We composite "marker on top" each click instead of re-rendering pdf.js
  // (which would re-blank the canvas and hide previous pins between clicks).
  const baseImageDataRef = useRef<ImageData | null>(null);

  const [pdf, setPdf] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pin, setPin] = useState<{ page: number; xPx: number; yPx: number } | null>(
    null,
  );
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
  const zoomFactor = ZOOM_LEVELS[zoomIndex];

  // When zoom changes, the canvas pixel dimensions change, so a pin's
  // stored (xPx, yPx) is in stale coordinates. Simplest UX : clear the
  // marker. In practice the user zooms in *before* clicking their parcel,
  // so this rarely matters. (Alternative would be to store as fraction
  // and rescale — keep that for later if it becomes a friction point.)
  useEffect(() => {
    setPin(null);
  }, [zoomFactor]);

  const canZoomIn = zoomIndex < ZOOM_LEVELS.length - 1;
  const canZoomOut = zoomIndex > 0;

  // ── Load the document once ──────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setPdf(null);
    setNumPages(0);
    setPageNum(1);
    setPin(null);
    baseImageDataRef.current = null;

    const task = pdfjsLib.getDocument({ url: pdfUrl });
    task.promise
      .then((doc) => {
        if (cancelled) {
          doc.destroy();
          return;
        }
        setPdf(doc);
        setNumPages(doc.numPages);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(String(e).slice(0, 200));
      });

    return () => {
      cancelled = true;
      // PDFDocumentLoadingTask.destroy is async but firing it cancels the
      // pending fetch/parse in pdf.js — safe to ignore the returned promise.
      void task.destroy();
    };
  }, [pdfUrl]);

  // ── Render the active page ──────────────────────────────────────────
  useEffect(() => {
    if (!pdf || !canvasRef.current) return;
    let cancelled = false;
    let renderTask: pdfjsLib.RenderTask | null = null;

    (async () => {
      const page = await pdf.getPage(pageNum);
      if (cancelled) return;

      // Effective scale = base × user zoom × DPR (capped at 2 to keep
      // canvas size sane on retina screens at high zoom).
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const effectiveScale = BASE_RENDER_SCALE * zoomFactor * dpr;
      const viewport = page.getViewport({ scale: effectiveScale });
      const canvas = canvasRef.current!;
      // `willReadFrequently: true` opt-in tells the browser to back the
      // canvas with software (not GPU) pixels — faster `getImageData`
      // reads, which we do every render to snapshot the pristine page
      // for marker overlay. Cuts the console warning + the actual CPU
      // cost on retina screens at high zoom.
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      // CSS size = logical pixels so layout/clicks line up with what the
      // user sees (ignoring DPR).
      canvas.style.width = `${viewport.width / dpr}px`;
      canvas.style.height = `${viewport.height / dpr}px`;

      // pdf.js 5+ requires the canvas itself in the render params (used for
       // size/DPR negotiation internally) — older v4 only needed canvasContext.
      renderTask = page.render({ canvas, canvasContext: ctx, viewport });
      await renderTask.promise;
      if (cancelled) return;

      // Snapshot the pristine page so we can overlay markers without
      // re-rendering pdf.js each time.
      baseImageDataRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
      // Replay any active pin that was on this page (e.g. user came back).
      if (pin && pin.page === pageNum) {
        drawMarker(ctx, pin.xPx, pin.yPx);
      }
    })();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
    // pageNum + pdf identity + zoom are inputs. Pin is replayed inside,
    // not a dep — we don't want to re-render pdf.js just because the pin
    // moved (that's a fast putImageData composite).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf, pageNum, zoomFactor]);

  // ── Click vs. drag (pan) detection ───────────────────────────────────
  // Standard "hand tool + pin" UX : a quick click drops a pin, but holding
  // and moving the mouse pans the scroll container instead. The threshold
  // (5 px) keeps shaky-mouse clicks classified as clicks. Without this,
  // agents on dense plans had to rely on the (low-contrast) scrollbars
  // exclusively to navigate large A0 scans.
  const dragStateRef = useRef<{
    startX: number;
    startY: number;
    scrollLeft: number;
    scrollTop: number;
    moved: boolean;
    pointerId: number;
  } | null>(null);
  const DRAG_THRESHOLD_PX = 5;

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const container = containerRef.current;
      if (!container) return;
      // Only react to primary button — let middle/right click do their
      // browser-default things (or nothing).
      if (e.button !== 0) return;
      dragStateRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
        moved: false,
        pointerId: e.pointerId,
      };
      // Capture the pointer so we keep getting move/up events even if the
      // cursor leaves the canvas mid-drag.
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const drag = dragStateRef.current;
      const container = containerRef.current;
      if (!drag || !container || drag.pointerId !== e.pointerId) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      drag.moved = true;
      // Reverse-direction scroll : drag right → content moves with cursor.
      container.scrollLeft = drag.scrollLeft - dx;
      container.scrollTop = drag.scrollTop - dy;
    },
    [],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const drag = dragStateRef.current;
      dragStateRef.current = null;
      if (!drag || drag.pointerId !== e.pointerId) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // Already released — fine.
      }
      // If the pointer moved, that was a pan — don't drop a pin.
      if (drag.moved) return;

      // Otherwise treat as a click → drop pin.
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx) return;
      const base = baseImageDataRef.current;
      if (!base) return; // page still rendering

      const rect = canvas.getBoundingClientRect();
      const cssX = e.clientX - rect.left;
      const cssY = e.clientY - rect.top;
      const xPx = (cssX / rect.width) * canvas.width;
      const yPx = (cssY / rect.height) * canvas.height;

      ctx.putImageData(base, 0, 0);
      drawMarker(ctx, xPx, yPx);
      setPin({ page: pageNum, xPx, yPx });

      // Position as page % — invariant of the user's current zoom, so
      // the export render (at a different scale) can place the marker
      // at the same logical spot.
      const xPct = xPx / canvas.width;
      const yPct = yPx / canvas.height;

      // Render a fresh export image — see EXPORT_RENDER_SCALE comment
      // above. This is async so we kick it off without blocking the UI.
      // The display canvas already has the marker drawn for instant
      // visual feedback; the parent's onPin handler is fine receiving
      // the image a few hundred ms later.
      if (pdf) {
        void renderForExport({ pdf, pageNum, xPct, yPct }).then((imageBase64) => {
          onPin({ page: pageNum, xPx, yPx, imageBase64 });
        });
      }
    },
    [onPin, pageNum, pdf],
  );

  // ── UI ──────────────────────────────────────────────────────────────
  const pageControls = useMemo(
    () => numPages > 1,
    [numPages],
  );

  if (loadError) {
    return (
      <div
        className={`relative w-full h-full grid place-items-center bg-ink-50 text-[12.5px] text-ink-700 p-4 text-center ${className ?? ""}`}
      >
        Impossible de charger le PDF.
        <br />
        <span className="font-mono text-[11px] text-ink-500 mt-1">
          {loadError}
        </span>
      </div>
    );
  }

  return (
    // Outer wrapper is a fixed-frame stage : the scroll lives inside it,
    // and the floating toolbars (zoom top-right, page nav bottom) are
    // siblings of the scroll container — that way they stay anchored to
    // the viewer corners regardless of how far the user has panned the
    // canvas. (The previous version put the toolbars inside the scroll
    // container as `absolute`, which means they scrolled away with the
    // content — exactly the bug we're fixing.)
    <div className={`relative w-full h-full ${className ?? ""}`}>
      {/* Scrollable canvas area. We stop 40 px above the bottom of the
          wrapper so the native horizontal scrollbar (rendered by the
          browser at the bottom edge of an `overflow:auto` element) isn't
          hidden behind the floating bottom bar. */}
      <div
        ref={containerRef}
        className="absolute inset-x-0 top-0 bottom-10 bg-ink-100 overflow-auto"
      >
        <div className="min-h-full grid place-items-center p-4">
          <canvas
            ref={canvasRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            // `touch-none` blocks the browser's native scroll-on-touch so our
            // pointer events drive panning consistently on tablets.
            className={`shadow-card hairline bg-white touch-none select-none ${
              busy
                ? "cursor-progress"
                : dragStateRef.current?.moved
                  ? "cursor-grabbing"
                  : "cursor-grab"
            }`}
          />
        </div>
      </div>

      {/* Floating zoom toolbar — anchored to the OUTER wrapper, not the
          scroll. High-contrast pill so it's unmissable on coloured PLU
          plans. `pointer-events-auto` re-enables clicks since some hosts
          set the wrapper to `pointer-events-none` for hit-passthrough. */}
      <div className="pointer-events-auto absolute top-3 right-3 z-10 flex items-center gap-1 bg-ink-900/90 backdrop-blur rounded-full p-1 shadow-card">
        <button
          type="button"
          onClick={() => setZoomIndex((i) => Math.max(0, i - 1))}
          disabled={!canZoomOut}
          className="w-9 h-9 rounded-full text-white text-[18px] font-medium grid place-items-center hover:bg-white/15 disabled:opacity-30 disabled:hover:bg-transparent transition"
          aria-label="Dézoomer"
          title="Dézoomer"
        >
          −
        </button>
        <span className="text-white text-[12px] font-mono tabular-nums px-2 min-w-[3.5rem] text-center">
          {Math.round(zoomFactor * 100)}%
        </span>
        <button
          type="button"
          onClick={() => setZoomIndex((i) => Math.min(ZOOM_LEVELS.length - 1, i + 1))}
          disabled={!canZoomIn}
          className="w-9 h-9 rounded-full text-white text-[18px] font-medium grid place-items-center hover:bg-white/15 disabled:opacity-30 disabled:hover:bg-transparent transition"
          aria-label="Zoomer"
          title="Zoomer"
        >
          +
        </button>
        <button
          type="button"
          onClick={() => setZoomIndex(DEFAULT_ZOOM_INDEX)}
          disabled={zoomIndex === DEFAULT_ZOOM_INDEX}
          className="ml-1 w-9 h-9 rounded-full text-white text-[11px] font-medium grid place-items-center hover:bg-white/15 disabled:opacity-30 disabled:hover:bg-transparent transition"
          aria-label="Réinitialiser le zoom"
          title="Réinitialiser le zoom (100%)"
        >
          1:1
        </button>
      </div>

      {/* Floating bottom bar — page nav + hint. Fixed h-10 to match the
          scroll container's bottom-10 inset, so the native horizontal
          scrollbar lives in the gap between the two and stays visible. */}
      <div className="absolute bottom-0 inset-x-0 z-10 h-10 flex items-center justify-between gap-3 px-3 bg-ink-900/85 backdrop-blur text-white text-[12px]">
        <div className="flex items-center gap-2">
          {pageControls ? (
            <>
              <Button
                variant="dark"
                size="sm"
                onClick={() => setPageNum((p) => Math.max(1, p - 1))}
                disabled={pageNum <= 1}
              >
                ←
              </Button>
              <span className="font-mono tabular-nums">
                Page {pageNum} / {numPages}
              </span>
              <Button
                variant="dark"
                size="sm"
                onClick={() => setPageNum((p) => Math.min(numPages, p + 1))}
                disabled={pageNum >= numPages}
              >
                →
              </Button>
            </>
          ) : (
            <span className="text-white/70">1 page</span>
          )}
        </div>
        <div className="text-[11.5px] text-white/80 hidden sm:block">
          {busy
            ? "Analyse en cours…"
            : "Clic = pin sur la parcelle · Clic + glisser = déplacer le plan"}
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

/**
 * Render the requested page at a fixed export scale to an offscreen
 * canvas, draw the marker at the given page-percentage position, and
 * return a JPEG base64 ready to send to the backend.
 *
 * Why fresh re-render instead of reusing the display canvas :
 *   1. The display canvas pixel dimensions vary with the user's zoom —
 *      a pre-export downscale would lose the marker at high zoom AND
 *      blur the legend, both of which break Claude's analysis.
 *   2. JPEG (vs the previous PNG) compresses zonage gradients ~10× tighter
 *      with no perceptible quality loss for Vision.
 */
async function renderForExport(args: {
  pdf: pdfjsLib.PDFDocumentProxy;
  pageNum: number;
  xPct: number;
  yPct: number;
}): Promise<string> {
  const page = await args.pdf.getPage(args.pageNum);
  const viewport = page.getViewport({ scale: EXPORT_RENDER_SCALE });
  const off = document.createElement("canvas");
  off.width = viewport.width;
  off.height = viewport.height;
  const ctx = off.getContext("2d");
  if (!ctx) throw new Error("Could not allocate offscreen canvas context.");
  await page.render({ canvas: off, canvasContext: ctx, viewport }).promise;

  // Marker drawn AFTER the page render so it sits on top of the zonage.
  // We scale it relative to canvas size so it stays visually consistent
  // even after downscaling (a 22 px radius at 2400 px wide ≈ 1 % of the
  // image — noticeable but not occluding).
  const markerScale = Math.max(off.width, off.height) / 1200;
  drawMarkerScaled(
    ctx,
    args.xPct * off.width,
    args.yPct * off.height,
    markerScale,
  );

  // Downscale + JPEG encode if oversized.
  const longest = Math.max(off.width, off.height);
  if (longest <= EXPORT_MAX_DIM) {
    return off
      .toDataURL("image/jpeg", EXPORT_JPEG_QUALITY)
      .replace(/^data:image\/jpeg;base64,/, "");
  }
  const ratio = EXPORT_MAX_DIM / longest;
  const w = Math.round(off.width * ratio);
  const h = Math.round(off.height * ratio);
  const small = document.createElement("canvas");
  small.width = w;
  small.height = h;
  const sctx = small.getContext("2d");
  if (!sctx) {
    return off
      .toDataURL("image/jpeg", EXPORT_JPEG_QUALITY)
      .replace(/^data:image\/jpeg;base64,/, "");
  }
  sctx.imageSmoothingEnabled = true;
  sctx.imageSmoothingQuality = "high";
  sctx.drawImage(off, 0, 0, w, h);
  return small
    .toDataURL("image/jpeg", EXPORT_JPEG_QUALITY)
    .replace(/^data:image\/jpeg;base64,/, "");
}

/** Like drawMarker, but with a scale factor applied to all dimensions. */
function drawMarkerScaled(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  scale: number,
): void {
  const radius = MARKER_RADIUS * scale;
  const ringWidth = MARKER_RING_WIDTH * scale;
  const haloWidth = MARKER_HALO_WIDTH * scale;
  const half = radius * 0.55;

  ctx.save();
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = haloWidth;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.stroke();

  ctx.strokeStyle = "#000000";
  ctx.lineWidth = ringWidth;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.stroke();

  ctx.lineCap = "round";
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = haloWidth;
  ctx.beginPath();
  ctx.moveTo(x - half, y);
  ctx.lineTo(x + half, y);
  ctx.moveTo(x, y - half);
  ctx.lineTo(x, y + half);
  ctx.stroke();
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = ringWidth;
  ctx.beginPath();
  ctx.moveTo(x - half, y);
  ctx.lineTo(x + half, y);
  ctx.moveTo(x, y - half);
  ctx.lineTo(x, y + half);
  ctx.stroke();

  ctx.fillStyle = "#000000";
  ctx.beginPath();
  ctx.arc(x, y, 1.5 * scale, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawMarker(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.save();
  // Layer 1 — wide white halo so the reticle is readable on any zonage
  // colour (light yellow, pink, dark green, hatching).
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = MARKER_HALO_WIDTH;
  ctx.beginPath();
  ctx.arc(x, y, MARKER_RADIUS, 0, Math.PI * 2);
  ctx.stroke();

  // Layer 2 — thin black ring on top.
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = MARKER_RING_WIDTH;
  ctx.beginPath();
  ctx.arc(x, y, MARKER_RADIUS, 0, Math.PI * 2);
  ctx.stroke();

  // Layer 3 — crosshair (white halo + black core).
  const half = MARKER_RADIUS * 0.55;
  ctx.lineCap = "round";
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = MARKER_HALO_WIDTH;
  ctx.beginPath();
  ctx.moveTo(x - half, y);
  ctx.lineTo(x + half, y);
  ctx.moveTo(x, y - half);
  ctx.lineTo(x, y + half);
  ctx.stroke();
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = MARKER_RING_WIDTH;
  ctx.beginPath();
  ctx.moveTo(x - half, y);
  ctx.lineTo(x + half, y);
  ctx.moveTo(x, y - half);
  ctx.lineTo(x, y + half);
  ctx.stroke();

  // Layer 4 — small central black dot for sub-pixel accuracy.
  ctx.fillStyle = "#000000";
  ctx.beginPath();
  ctx.arc(x, y, 1.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
